/**
 * UploadZone component
 *
 * Drag-and-drop + click-to-browse file upload for audio files.
 * Accepted formats: MP3, WAV, FLAC.
 * 
 * Design: Warm dark palette with amber accents.
 */

import React, { useCallback, useRef, useState } from 'react';

interface UploadZoneProps {
  onFileSelected: (file: File) => void;
  isUploading: boolean;
  uploadProgress?: number;
}

const ACCEPTED_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/flac', 'audio/x-flac'];
const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.flac'];

function isValidAudio(file: File): boolean {
  return (
    ACCEPTED_TYPES.includes(file.type) ||
    ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
  );
}

export const UploadZone: React.FC<UploadZoneProps> = ({
  onFileSelected,
  isUploading,
  uploadProgress = 0,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      setDragError(null);

      const file = e.dataTransfer.files[0];
      if (!file) return;

      if (!isValidAudio(file)) {
        setDragError('Only MP3, WAV, and FLAC files are accepted.');
        return;
      }
      onFileSelected(file);
    },
    [onFileSelected]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!isUploading) inputRef.current?.click();
  }, [isUploading]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!isValidAudio(file)) {
        setDragError('Only MP3, WAV, and FLAC files are accepted.');
        return;
      }
      setDragError(null);
      onFileSelected(file);
      e.target.value = '';
    },
    [onFileSelected]
  );

  return (
    <div className="w-full">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload audio file"
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        className={[
          'relative flex flex-col items-center justify-center',
          'w-full h-48 rounded-2xl border-2 border-dashed',
          'cursor-pointer transition-all duration-300',
          'select-none outline-none',
          isDragging
            ? 'border-amber-500 bg-amber-500/10 scale-[1.02]'
            : 'border-studio-700 bg-studio-800/50 hover:border-amber-500/50 hover:bg-studio-800',
          isUploading ? 'pointer-events-none opacity-60' : '',
        ].join(' ')}
      >
        {/* Icon */}
        <div
          className={[
            'mb-4 w-16 h-16 rounded-full flex items-center justify-center',
            'transition-all duration-300',
            isDragging ? 'bg-amber-500/20' : 'bg-studio-700',
          ].join(' ')}
        >
          <svg
            className={isDragging ? 'text-amber-500' : 'text-stone-500'}
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>

        {isUploading ? (
          <div className="text-center px-4">
            <p className="text-amber-500 font-semibold mb-3">
              Processing audio...
            </p>
            <div className="w-48 h-1.5 bg-studio-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-stone-500 text-sm mt-2 font-mono">{uploadProgress}%</p>
          </div>
        ) : (
          <div className="text-center px-4">
            <p className="text-stone-200 font-medium mb-1">
              {isDragging ? 'Drop to upload' : 'Drop audio file here'}
            </p>
            <p className="text-stone-500 text-sm">
              or{' '}
              <span className="text-amber-500 underline">browse files</span>
            </p>
            <p className="text-stone-600 text-xs mt-2">
              MP3, WAV, FLAC — max 50 MB
            </p>
          </div>
        )}
      </div>

      {dragError && (
        <p className="mt-2 text-coral-400 text-sm text-center">{dragError}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
};
